import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import AppLayout from '../../SideBar';
import strings from '../../../constant/strings';
import { LoadingContainer, NoDetailsContainer } from '../../../assets/styles/CommonStyles';
import {
  cancelPreorderApi, getMyPreorderApi, payPreorderBalanceApi, setPreorderAddressApi,
} from '../../../services/preorderService';
import {
  Card, FieldGrid, Muted, Notice, OutlineButton, Page, PrimaryButton, ScheduleTable,
  SectionTitle, StatusPill, SummaryLine, TopBar,
} from '../../Preorder/styles';
import {
  canCancel, canPayEarly, formatDate, formatPaise, formatWindow, isPaymentDue,
  nextInstalment, statusLabel,
} from '../../Preorder/utils';

const toastConfig = {
  position: 'bottom-center', closeButton: false, autoClose: 2500,
  style: { width: 'auto', borderRadius: 8 },
};

/**
 * Every action on this page is switched off for now, so an order can be read
 * but not changed.
 *
 * One flag rather than several, because they go live together: paying needs a
 * webhook Razorpay can reach, and cancelling or editing an address on an order
 * nobody can pay for is not much use on its own. Flip it to true and the page
 * is fully operable again - nothing else has to change.
 *
 * The link back to the list is deliberately NOT covered by this: disabling it
 * would leave someone on a page with no way off it.
 */
const ACTIONS_ENABLED = false;

const INSTALMENT_LABEL = {
  deposit: 'Deposit', balance: 'Balance', full: 'Full amount',
};

/**
 * One pre-order: what was agreed, what has been paid, and what is still owed.
 *
 * Paying an instalment opens Razorpay's hosted link rather than the in-page
 * checkout, because the server raises the same link whether the patient clicks
 * here or follows the one we emailed - so there is only ever one live link per
 * instalment and it cannot be paid twice.
 */
const PreorderDetailContent = () => {
  const history = useHistory();
  const { id } = useParams();
  const token = useSelector((state) => state.auth.token);

  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('loading');
  const [busy, setBusy] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [editingAddress, setEditingAddress] = useState(false);
  const [address, setAddress] = useState({
    line1: '', line2: '', city: '', state: '', pincode: '', country: 'India',
  });

  const load = useCallback(() => getMyPreorderApi({ token, id }).then((res) => {
    if (res.response_type === 'fail') { setStatus('error'); return; }
    setOrder(res.response?.data);
    setStatus('ready');
  }), [token, id]);

  useEffect(() => { load(); }, [load]);

  const onPay = async () => {
    setBusy('pay');
    const res = await payPreorderBalanceApi({ token, id });
    setBusy('');
    if (res.response_type === 'fail') {
      toast.error(res.response?.message || strings.preorderGenericError, toastConfig);
      return;
    }
    const url = res.response?.data?.payUrl;
    if (url) {
      // Razorpay's hosted page. Same tab, so returning lands back here.
      window.location.href = url;
      return;
    }
    toast.error(strings.preorderGenericError, toastConfig);
  };

  const onSaveAddress = async () => {
    setBusy('address');
    const res = await setPreorderAddressApi({
      token, id, shippingAddress: { ...address, pincode: String(address.pincode).trim() },
    });
    setBusy('');
    if (res.response_type === 'fail') {
      toast.error(res.response?.message || strings.preorderGenericError, toastConfig);
      return;
    }
    setEditingAddress(false);
    toast.success(strings.addressSaved, toastConfig);
    await load();
  };

  const onCancel = async () => {
    setBusy('cancel');
    const res = await cancelPreorderApi({ token, id, reason });
    setBusy('');
    setCancelOpen(false);
    if (res.response_type === 'fail') {
      toast.error(res.response?.message || strings.preorderGenericError, toastConfig);
      return;
    }
    toast.success(strings.preorderCancelled, toastConfig);
    await load();
  };

  if (status === 'loading') return <LoadingContainer>{strings.loading}</LoadingContainer>;
  if (status === 'error') return <NoDetailsContainer><Muted>{strings.preorderGenericError}</Muted></NoDetailsContainer>;

  const paidPct = order.amounts?.total
    ? Math.round((order.paidAmount / order.amounts.total) * 100)
    : 0;
  const hasAddress = Boolean(order.shippingAddress && order.shippingAddress.line1);
  // Changeable right up to dispatch; after that it is a courier's problem.
  const addressEditable = !['shipped', 'delivered'].includes(order.status)
    && !order.status.startsWith('cancelled') && !order.status.startsWith('refund');
  const addressComplete = address.line1.trim() && address.city.trim()
    && address.state.trim() && /^[1-9][0-9]{5}$/.test(String(address.pincode).trim());
  const paymentDue = isPaymentDue(order);
  const payEarly = canPayEarly(order);
  const next = nextInstalment(order);

  return (
    <Page style={{ padding: 0, maxWidth: 'none' }}>
      <TopBar>
        <OutlineButton onClick={() => history.push('/patient/preorders')}>{strings.myPreorders}</OutlineButton>
        <StatusPill data-tone={paymentDue ? 'warn' : 'good'}>
          {statusLabel(order.status)}
        </StatusPill>
      </TopBar>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: 20 }}
        className="preorder-detail-grid">
        <div style={{ display: 'grid', gap: 20 }}>
          <Card>
            <SectionTitle>{order.productTitle}</SectionTitle>
            <Muted>{strings.orderNumber}: {order.orderNumber}</Muted>
            <Muted style={{ marginTop: 4 }}>{strings.quantity}: {order.qty}</Muted>
            <Muted style={{ marginTop: 4 }}>
              {strings.expectedDispatch}: {formatWindow(order.expectedShipFrom, order.expectedShipTo)}
            </Muted>
            {order.reservedUntil ? (
              <Notice>
                {strings.reservationHeldNote} ({formatDate(order.reservedUntil)})
              </Notice>
            ) : null}
            {order.cancellation?.at ? (
              <Notice data-tone="error">
                {strings.preorderCancelled} {order.cancellation.reason ? `- ${order.cancellation.reason}` : ''}
              </Notice>
            ) : null}
          </Card>

          <Card>
            <SectionTitle>{strings.paymentSchedule}</SectionTitle>
            <ScheduleTable>
              <div className="head">
                <div>{strings.instalment}</div>
                <div>{strings.amount}</div>
                <div>{strings.dueDate}</div>
                <div />
              </div>
              {(order.schedule || []).map((item) => (
                <div key={item.seq}>
                  <div>{INSTALMENT_LABEL[item.kind] || item.kind}</div>
                  <div style={{ fontWeight: 600 }}>{formatPaise(item.amount)}</div>
                  <div>
                    {formatDate(item.dueAt)}
                    {item.deadlineAt ? (
                      <Muted style={{ fontSize: 12 }}>by {formatDate(item.deadlineAt)}</Muted>
                    ) : null}
                  </div>
                  <div>
                    <StatusPill data-tone={item.status === 'paid' ? 'good' : (item.status === 'cancelled' ? 'bad' : 'warn')}>
                      {statusLabel(item.status)}
                    </StatusPill>
                  </div>
                </div>
              ))}
            </ScheduleTable>
          </Card>

          {/* Asked for here rather than at reservation, because dispatch is
              weeks away and this is when the patient actually knows it. */}
          <Card>
            <SectionTitle>{strings.deliveryAddress}</SectionTitle>
            {!editingAddress ? (
              <>
                {hasAddress ? (
                  <Muted>
                    {[order.contact?.name, order.shippingAddress.line1, order.shippingAddress.line2,
                      order.shippingAddress.city, order.shippingAddress.state,
                      order.shippingAddress.pincode, order.shippingAddress.country]
                      .filter(Boolean).join(', ')}
                  </Muted>
                ) : (
                  <Notice>
                    {ACTIONS_ENABLED ? strings.addressNeededBeforeDispatch : strings.actionsNotOpenYet}
                  </Notice>
                )}
                {addressEditable ? (
                  <OutlineButton
                    style={{ marginTop: 12 }}
                    disabled={!ACTIONS_ENABLED}
                    onClick={() => { setAddress({ ...address, ...(order.shippingAddress || {}) }); setEditingAddress(true); }}
                  >
                    {hasAddress ? strings.changeAddress : strings.addAddress}
                  </OutlineButton>
                ) : null}
              </>
            ) : (
              <>
                <FieldGrid>
                  <TextField
                    className="full" size="small" label={strings.addressLine1} required
                    value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                  />
                  <TextField
                    className="full" size="small" label={strings.addressLine2}
                    value={address.line2 || ''} onChange={(e) => setAddress({ ...address, line2: e.target.value })}
                  />
                  <TextField
                    size="small" label={strings.city} required
                    value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })}
                  />
                  <TextField
                    size="small" label={strings.state} required
                    value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })}
                  />
                  <TextField
                    size="small" label={strings.pincode} required
                    value={address.pincode}
                    error={Boolean(address.pincode) && !/^[1-9][0-9]{5}$/.test(address.pincode)}
                    onChange={(e) => setAddress({ ...address, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  />
                </FieldGrid>
                <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                  <PrimaryButton onClick={onSaveAddress} disabled={!addressComplete || busy === 'address'}>
                    {busy === 'address' ? strings.loading : strings.saveAddress}
                  </PrimaryButton>
                  <OutlineButton onClick={() => setEditingAddress(false)}>{strings.keepPreorder}</OutlineButton>
                </div>
              </>
            )}
          </Card>
        </div>

        <div>
          <Card>
            <SectionTitle>{strings.total}</SectionTitle>
            <SummaryLine>
              <span>{strings.paidSoFar}</span>
              <span>{formatPaise(order.paidAmount)} ({paidPct}%)</span>
            </SummaryLine>
            <SummaryLine>
              <span>{strings.stillDue}</span>
              <span style={{ color: order.dueAmount > 0 ? '#96670A' : undefined }}>
                {formatPaise(order.dueAmount)}
              </span>
            </SummaryLine>
            <SummaryLine data-strong="true">
              <span>{strings.total}</span>
              <span>{formatPaise(order.amounts?.total)}</span>
            </SummaryLine>

            {/* Nothing was collected when the order was placed, so for most of
                its life the patient owes the full amount without being chased.
                Only once it is actually due do we ask; before that we say when,
                and offer paying early as a quiet option. */}
            {paymentDue ? (
              <>
                <PrimaryButton
                  fullWidth
                  onClick={onPay}
                  disabled={!ACTIONS_ENABLED || Boolean(busy)}
                  style={{ marginTop: 12 }}
                >
                  {busy === 'pay' ? strings.loading : `${strings.payNow} ${formatPaise(order.dueAmount)}`}
                </PrimaryButton>
                <Muted style={{ marginTop: 8 }}>
                  {ACTIONS_ENABLED ? strings.payBeforeDispatchNote : strings.paymentNotOpenYet}
                </Muted>
              </>
            ) : null}

            {payEarly ? (
              <>
                <Notice style={{ marginTop: 12 }}>
                  {strings.paymentWillBeRequested} {formatDate(next?.dueAt)}.
                  {' '}{ACTIONS_ENABLED ? strings.payEarlyNote : strings.paymentNotOpenYet}
                </Notice>
                <OutlineButton fullWidth onClick={onPay} disabled={!ACTIONS_ENABLED || Boolean(busy)}>
                  {busy === 'pay' ? strings.loading : `${strings.payEarly} ${formatPaise(order.dueAmount)}`}
                </OutlineButton>
              </>
            ) : null}

            {canCancel(order) ? (
              <OutlineButton
                fullWidth
                onClick={() => setCancelOpen(true)}
                disabled={!ACTIONS_ENABLED || Boolean(busy)}
                style={{ marginTop: 10 }}
              >
                {strings.cancelPreorder}
              </OutlineButton>
            ) : null}
          </Card>
        </div>
      </div>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle style={{ fontFamily: 'Poppins, sans-serif', fontSize: 17 }}>
          {strings.cancelPreorder}
        </DialogTitle>
        <DialogContent>
          <Muted style={{ marginBottom: 16 }}>{strings.cancelPreorderConfirm}</Muted>
          <TextField
            fullWidth size="small" multiline minRows={2}
            label={strings.cancelReasonLabel}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 500))}
          />
        </DialogContent>
        <DialogActions>
          <OutlineButton onClick={() => setCancelOpen(false)}>{strings.keepPreorder}</OutlineButton>
          <PrimaryButton onClick={onCancel} disabled={busy === 'cancel'}>
            {busy === 'cancel' ? strings.loading : strings.cancelPreorder}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <style>{`
        @media (max-width: 900px) {
          .preorder-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </Page>
  );
};

const PreorderDetail = () => (
  <Grid container spacing={0}>
    <AppLayout>
      <PreorderDetailContent />
    </AppLayout>
  </Grid>
);

export default PreorderDetail;
